import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { shouldExcludeFromAnalytics } from '@/lib/analyticsExclusions';


export const useGoogleAnalytics = () => {
  const location = useLocation();
  const pageViewSentRef = useRef<string | null>(null);
  const initialPathRef = useRef<string | null>(null);
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    if (isDev) {
      console.log('[GA Debug] useEffect triggered, path:', location.pathname);
    }
    
    // Skip tracking for admin pages,auth pages, and Lovable dev environments
    if (shouldExcludeFromAnalytics(location.pathname)) {
      if (isDev) console.log('[GA Debug] Skipping excluded route or dev environment');
      return;
    }

    const currentPath = location.pathname + location.search;

    // gtag config in index.html auto-sends the initial pageview, so skip the first route to avoid double-counting.
    if (initialPathRef.current === null) {
      initialPathRef.current = currentPath;
      pageViewSentRef.current = currentPath;
      if (isDev) console.log('[GA Debug] Initial path — auto-pageview handled by gtag config, skipping hook send for:', currentPath);
      return;
    }

    // Don't re-track if already sent for this path
    if (pageViewSentRef.current === currentPath) {
      if (isDev) console.log('[GA Debug] Already tracked:', currentPath);
      return;
    }

    let engagementDetected = false;

    const sendPageView = () => {
      if (engagementDetected || pageViewSentRef.current === currentPath) return;
      engagementDetected = true;
      pageViewSentRef.current = currentPath;
      
      if (isDev) console.log('[GA Debug] Sending page view:', currentPath);
      
      if (typeof window.gtag === 'function') {
        window.gtag('config', 'G-RM33W2EM91', {
          page_path: currentPath,
        });
      }
    };

    // Engagement listeners
    const handleScroll = () => {
      if (window.scrollY > 50) sendPageView();
    };
    
    const handleClick = () => sendPageView();
    const handleMouseMove = () => sendPageView();
    const handleTouch = () => sendPageView();

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleClick, { once: true });
    window.addEventListener('mousemove', handleMouseMove, { once: true });
    window.addEventListener('touchstart', handleTouch, { once: true });

    // Fallback: 3-second minimum time threshold (catches readers who don't interact)
    const timeoutId = setTimeout(sendPageView, 3000);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouch);
      clearTimeout(timeoutId);
    };
  }, [location]);
};
